import React, { useEffect, useState, useRef, useCallback } from "react";

import { useClinicState } from "../../../clinic/clinicStateContext";
import { ClinicLoading } from "../uiTemplates/clinicLoading";
import {
  styled,
  Alert,
  IconButton,
  Table,
  TableCell,
  TableRow,
  Link,
  TextField,
  TableHead,
  TableBody,
  InputLabel,
  MenuItem,
} from "@mui/material";
import { ReactComponent as ThumbsUp } from "fe-web-components/assets/thumbsUpIcon.svg";
import { ReactComponent as ThumbsDown } from "fe-web-components/assets/thumbsDownIcon.svg";
import { ReactComponent as CheckCircleIcon } from "fe-web-components/assets/patientCard/checkCircleIcon.svg";
import { ReactComponent as XCircleIcon } from "fe-web-components/assets/patientCard/xCircleIcon.svg";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { ClinicConfigKeys } from "fe-common/graphql/clinic/clinicConfigKeys";
import { CustomFeedbackOption } from "@kahunmedical/common-configuration/dist/configuration/dialogueOptions";
import { ClinicColors } from "fe-web-components/styles/clinicTheme";
import { ClinicDateTimePicker } from "../elements/clinicDateTimePicker";
import { PrivatePlaceholder } from "fe-web-components/patientCard/PrivatePlaceholder";
import { RagDao } from "dialogue/src/graphql/ragDao";
import { FeedbackReportExchange } from "dialogue/src/graphql/types/graphql";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import ReportFilterForm from "./reportFilterForm";
import { openInDialogue } from "./utils";
import { AnnotatedText } from "fe-web-components/references/AnnotatedText";
import FeedbackDisplay from "./feedbackDisplay";
import ExportsDialog, { ExportFormat } from "./export/exportsDialog";
import { SessionType } from "dialogue/src/types/types";
//region [[ Styles ]]

const ReportTableStyled = styled(Table)({
  width: "100%",
  borderCollapse: "separate",
  "& th": {
    lineHeight: "1.2",
  },
  "&:dir(rtl) td, &:dir(rtl) th": {
    textAlign: "right",
  },
  "& .customFeedbackCell": {
    maxWidth: 100,
  },
  "& .link-button": {
    width: 16,
    height: 16,
  },
  "& .thumbs-up": {
    width: 17,
    height: 17,
    color: "#1ca21c",
  },
  "& .thumbs-down": {
    width: 17,
    height: 17,
    color: "#ff3b3b",
  },
  "& .check-circle": {
    width: 19,
    height: 19,
    fill: "#1ca21c",
  },
  "& .x-circle": {
    width: 17,
    height: 17,
    fill: "#ff3b3b",
  },
});

const ReportRowStyled = styled(TableRow)({
  cursor: "pointer",
  background: "white",
  "& .question, & .answer": {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  "& .question": { maxWidth: 180 },
  "& .answer": {
    maxWidth: 480,
  },
  "&.expanded": {
    "& td": {
      background: "#FFF",
      borderBottomWidth: 0,
    },
    "& .answer, & .question": {
      whiteSpace: "wrap",
    },
  },
});

const ReportRowData = styled(TableRow)({
  cursor: "pointer",
  background: "white",
  "& td": {
    borderTop: "1px dotted #bbb",
    borderBottomWidth: 2,
  },
});

const CaseDataRow = styled(TableCell)(({ theme }) => ({
  padding: 0,
  background: ClinicColors.lightBackground,
  borderTop: `8px solid #F7F7F9`,
  "& div.case-data": {
    borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex",
    gap: 8,
    alignItems: "center",
    justifyContent: "space-between",
    padding: "6px 16px",
  },
}));

const MessageDiv = styled("div")({
  margin: "80px 0",
  fontWeight: "bold",
  paddingBottom: 8,
  textAlign: "center",
});

const FormHeader = styled("div")({
  display: "flex",
  alignItems: "start",
  justifyContent: "space-between",
  gap: 16,
});
//endregion [[ Styles ]]

//region [[ Props ]]
type Filter = {
  user: string;
  fromDate: string;
  toDate: string;
  sessionType?: SessionType;
};
interface FilterFormProps {
  onSubmit: (value: Filter) => void;
  onReset: () => void;
  disabled: boolean;
  locale: string;
}
//endregion [[ Props ]]
interface ReportRowProps {
  data: FeedbackReportExchange;
  ragApp?: string;
  columnCount: number;
  customFeedback: CustomFeedbackOption[];
}

//region [[ Functions ]]

//endregion [[ Functions ]]
const FilterForm = ({
  onSubmit,
  onReset,
  disabled,
  locale,
}: FilterFormProps) => {
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [user, setUser] = useState<string>("");
  const [sessionType, setSessionType] = useState<SessionType | "ALL">("ALL");

  return (
    <ReportFilterForm
      onSubmit={() =>
        onSubmit({
          user,
          fromDate,
          toDate,
          sessionType: sessionType === "ALL" ? undefined : sessionType,
        })
      }
      onReset={() => {
        setUser("");
        setFromDate("");
        setToDate("");
        setSessionType(undefined);
        onReset();
      }}
      disabled={disabled}
    >
      <TextField
        id="user-filter"
        size="small"
        label="User email"
        disabled={disabled}
        variant="outlined"
        value={user}
        onChange={(e) => setUser(e.currentTarget.value)}
      />
      <ClinicDateTimePicker
        size="small"
        label="From"
        showTime={false}
        value={fromDate && new Date(fromDate)}
        onChange={(dateVal) => {
          setFromDate(dateVal?.toISOString());
        }}
        locale={locale}
      />
      <ClinicDateTimePicker
        size="small"
        label="To"
        showTime={false}
        value={toDate && new Date(toDate)}
        onChange={(dateVal) => {
          setToDate(dateVal?.toISOString());
        }}
        locale={locale}
      />
      <FormControl>
        <InputLabel id="sessionType-label">Type</InputLabel>
        <Select
          labelId="sessionType-label"
          id="sessionType-select"
          value={sessionType}
          label="Age"
          onChange={(e) => setSessionType(e.target.value as SessionType)}
          variant="outlined"
          size="small"
          style={{ minWidth: 100 }}
        >
          <MenuItem value={"ALL"} key={"allTypes"} selected={!sessionType}>
            ALL
          </MenuItem>
          {Object.keys(SessionType).map((key) => (
            <MenuItem value={SessionType[key]} key={"type" + key}>
              {key}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </ReportFilterForm>
  );
};

export const FeedbackReport = () => {
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [fileName, setFileName] = useState<string>("");
  const [
    {
      loading: clinicLoading,
      clinicData,
      rtlDisplay,
      clinicConfig,
      privateMode,
    },
  ] = useClinicState();
  const [reportData, setReportData] = useState<FeedbackReportExchange[]>(null);
  const [customFeedback, setCustomFeedback] = useState<CustomFeedbackOption[]>(
    null,
  );
  const [columnCount, setColumnCount] = useState(0);
  const hasFetched = useRef(false);
  const ragApp = clinicConfig[ClinicConfigKeys.RagApp];
  const DEFAULT_COLUMN_COUNT = 4;

  const fetchData = useCallback(
    async (filter?: Filter) => {
      try {
        setErrorMsg("");
        setLoading(true);
        const ragDao = new RagDao();
        const data = await ragDao.getFeedBackReport(
          clinicData.id,
          filter?.user,
          filter?.fromDate,
          filter?.toDate,
          filter?.sessionType,
        );

        const fDate = filter?.fromDate
          ? new Date(filter?.fromDate)
          : new Date();
        const tDate = filter?.toDate ? new Date(filter?.toDate) : new Date();
        const fromMonth = fDate.getMonth() + 1;
        const toMonth = tDate.getMonth() + 1;
        const fromDay = fDate.getDate();
        const toDay = tDate.getDate();
        setFileName(
          `clinicFeedbacks_${clinicData?.name}_${fromDay}${
            fromMonth === toMonth ? "" : "_" + fromMonth
          }-${toDay}_${toMonth}`,
        );

        setReportData(
          data.sort((a, b) => {
            let c = new Date(a.date).getTime();
            let d = new Date(b.date).getTime();
            return d - c;
          }),
        );
      } catch (error) {
        console.error("Error fetching feedback report:", error);
        setErrorMsg("Could not fetch feedback report.");
      } finally {
        setLoading(false);
      }
    },
    [clinicData.id, clinicData.name],
  );

  useEffect(() => {
    if (hasFetched.current || clinicLoading || !clinicData.id) return;
    const customFeedbackOptions = clinicConfig?.dialogue?.customFeedbackOptions;
    setCustomFeedback(customFeedbackOptions);
    setColumnCount(DEFAULT_COLUMN_COUNT + (customFeedbackOptions?.length || 0));
    // hasFetched.current = true; // Prevent subsequent fetches
    // fetchData();
  }, [clinicLoading, clinicData.id, fetchData, clinicConfig]);

  let sessionId = "";

  return (
    <>
      <FormHeader>
        <FilterForm
          onSubmit={(filter) => {
            fetchData(filter);
          }}
          onReset={() => {
            //fetchData();
          }}
          disabled={loading}
          locale={clinicConfig.defaultLocale}
        />
        <ExportsDialog
          data={reportData?.map(({ feedback, ...exchange }) => ({
            ...exchange,
            feedback: JSON.stringify(feedback),
          }))}
          fileName={fileName}
          formats={[ExportFormat.XLSX]}
          sheetName={"Feedbacks"}
          disabled={clinicLoading || loading || reportData === null}
        />
      </FormHeader>
      {(clinicLoading || loading) && (
        <ClinicLoading title={"Loading feedback report"} />
      )}
      {errorMsg && (
        <Alert variant={"filled"} severity={"error"}>
          {errorMsg}
        </Alert>
      )}
      {!errorMsg && !loading && (
        <>
          {reportData?.length > 0 && (
            <MessageDiv
              style={{
                borderBottom: "1px solid #ddd",
                margin: "8px 0",
              }}
            >
              Total feedbacks: {reportData.length}
            </MessageDiv>
          )}
          {!reportData ? (
            <MessageDiv>
              Please press 'Generate' to create the report
            </MessageDiv>
          ) : !reportData.length ? (
            <MessageDiv>No feedback data found.</MessageDiv>
          ) : (
            <ReportTableStyled dir={rtlDisplay ? "rtl" : "ltr"}>
              <TableHead
                style={{
                  position: "sticky",
                  top: 0,
                  background: "#f7f7f9",
                  zIndex: 100,
                }}
              >
                <TableRow>
                  <TableCell>Question</TableCell>
                  <TableCell>Answer</TableCell>
                  <TableCell>Feedback</TableCell>
                  {customFeedback.map((option, index) => (
                    <TableCell
                      className={"customFeedbackCell"}
                      key={"customColumn" + index}
                    >
                      {option.text}
                    </TableCell>
                  ))}
                  <TableCell>Link</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.map((feedbackExchange, index) => {
                  let sessionData = null;
                  let isoDate = new Date(feedbackExchange.date);
                  if (sessionId !== feedbackExchange.sessionId) {
                    sessionData = (
                      <TableRow>
                        <CaseDataRow colSpan={columnCount}>
                          <div className={"case-data"}>
                            {isoDate.toLocaleString("en-GB")}
                            <Link
                              onClick={() => openInDialogue(sessionId, ragApp)}
                            >
                              {feedbackExchange.title}
                            </Link>
                            <div>
                              {privateMode ? (
                                <PrivatePlaceholder>
                                  Private information
                                </PrivatePlaceholder>
                              ) : (
                                feedbackExchange.user
                              )}
                            </div>
                          </div>
                        </CaseDataRow>
                      </TableRow>
                    );
                    sessionId = feedbackExchange.sessionId;
                  }
                  return (
                    <React.Fragment key={"row" + index}>
                      {sessionData}
                      <ReportRow
                        data={feedbackExchange}
                        ragApp={ragApp}
                        columnCount={columnCount}
                        customFeedback={customFeedback}
                      />
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </ReportTableStyled>
          )}
        </>
      )}
    </>
  );
};

const ReportRow = ({
  data,
  ragApp,
  columnCount,
  customFeedback,
}: ReportRowProps) => {
  const [expanded, setExpanded] = useState<boolean>(false);

  return (
    <>
      <ReportRowStyled
        onClick={() => setExpanded(!expanded)}
        className={expanded ? "expanded" : ""}
      >
        <TableCell className={"question"}>{data.question}</TableCell>
        <TableCell className={"answer"}>
          {expanded ? (
            <AnnotatedText
              text={data.answer}
              onFootnoteClick={null}
              onShowMore={null}
            />
          ) : (
            data.answer
          )}
        </TableCell>
        <TableCell>
          {data.feedback?.positive && <ThumbsUp className={"thumbs-up"} />}
          {data.feedback?.positive === false && (
            <ThumbsDown className={"thumbs-down"} />
          )}
        </TableCell>
        {customFeedback.map((option, index) => (
          <TableCell key={"customColumn" + index}>
            {data.feedback?.customFeedback?.[index]?.value && (
              <CheckCircleIcon className={"check-circle"} />
            )}
            {data.feedback?.customFeedback?.[index]?.value === false && (
              <XCircleIcon className={"x-circle"} />
            )}
          </TableCell>
        ))}
        <TableCell>
          <IconButton
            size={"small"}
            onClick={() => openInDialogue(data.sessionId, ragApp)}
          >
            <OpenInNewIcon className={"link-button"} />
          </IconButton>
        </TableCell>
      </ReportRowStyled>
      {expanded && (
        <ReportRowData onClick={() => setExpanded(!expanded)}>
          <TableCell colSpan={columnCount}>
            <FeedbackDisplay feedback={data.feedback} />
          </TableCell>
        </ReportRowData>
      )}
    </>
  );
};
